/**
 * Supplier Platform T&Cs — the contract served by Baqaal's vendor-terms module
 * (BAQ-1779). Mirrors `src/models/vendor-terms/interfaces` there; keep the two
 * in step.
 */

export enum TermsStatusEnum {
	/** Published, inside the notice window, not yet accepted. Banner only. */
	NOTICE = "notice",
	/** Past the effective date, or never accepted at all. Blocked. */
	BLOCKED = "blocked",
	/** Accepted the current version. Nothing shown. */
	ACCEPTED = "accepted",
}

export enum VendorRoleEnum {
	SUPER_ADMIN = "super_admin",
	ADMIN = "admin",
}

/** How the document viewer was opened. Decides the close control and the footer. */
export enum TermsViewerModeEnum {
	/** From the notice banner: closeable, consent + "Accept now". */
	NOTICE = "notice",
	/** From the blocking modal: no close control, consent + "Accept". */
	BLOCKING = "blocking",
	/** From the sidebar link: closeable, no footer, shows the accepted version. */
	READ_ONLY = "readOnly",
}

export interface iCurrentTerms {
	documentType: string;
	version: string;
	title: string;
	contentUrl: string;
	keyChanges: string[];
	publishedAt: string;
	/**
	 * Already clamped by Baqaal to `max(cms.effectiveAt, publishedAt +
	 * minNoticeDays)`. Render this, never a locally computed date — the gate
	 * enforces this one.
	 */
	effectiveDate: string;
	acceptedVersion: string | null;
	status: TermsStatusEnum;
	/** Whole days until `effectiveDate`. Not currently rendered — the approved banner copy has no countdown. */
	daysRemaining: number;
	role: VendorRoleEnum;
	/** Server-decided. Render the accept control from this, never from `role`. */
	canAccept: boolean;
	/**
	 * When the vendor's shared 24h reminder cooldown expires, or null when no
	 * reminder is outstanding.
	 *
	 * Optional because Baqaal does not send it yet (BAQ ticket pending). Until it
	 * does, the "already reminded" state is only known to the tab that sent the
	 * reminder — a new tab, another device or a colleague sees the button enabled
	 * again. The cooldown is per vendor, so this has to come from the server;
	 * caching it locally would be wrong for everyone else on the team.
	 */
	reminderCooldownExpiresAt?: string | null;
	/** Name of a Super Admin to credit in the confirmation after a reload. */
	superAdminName?: string | null;
}

export interface iTermsContent {
	version: string;
	title: string;
	/** A sanitized HTML **fragment**. The portal owns its typography. */
	html: string;
}

export interface iTermsVersion {
	version: string;
	title: string;
	publishedAt: string;
	effectiveDate: string;
	contentUrl: string;
}

export interface iAcceptTermsResult {
	version: string;
	acceptedAt: string;
}

export interface iRemindResult {
	cooldownExpiresAt: string;
	/** First name of a Super Admin who was emailed. Note: not `superAdminName`. */
	recipientName: string;
}

/**
 * Why the gate could not be evaluated. Both are non-blocking: the server guard
 * itself fails open, so the portal must too.
 */
export enum TermsUnavailableReasonEnum {
	/** 404 — no version published yet. The expected state before Legal publishes. */
	NOT_PUBLISHED = "notPublished",
	/** 503 — the CMS is unreachable. */
	SERVICE_UNAVAILABLE = "serviceUnavailable",
}
